import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import {
  AlertTriangle,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Inbox,
  Loader,
  Search,
  Trash2,
} from 'lucide-react';

import {
  fetchDocumentTypes,
  getSignedDocumentUrl,
  listAll,
  ListAllParams,
  PersonalizedDocumentRow,
  PersonalizedDocumentType,
  updateWindow,
  withdrawDocument,
} from '../../_services/personalizedDocuments.service';

const PAGE_SIZE = 25;

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes && bytes !== 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Convert a stored datetime ("YYYY-MM-DD HH:mm:ss" or ISO) into the
// "YYYY-MM-DDTHH:mm" value a datetime-local input expects, without applying
// any timezone math (the stored value is already tenant-local wall-clock).
function toLocalInput(value: string | null | undefined): string {
  if (!value) return '';
  const m = String(value).replace('T', ' ').match(/^(\d{4}-\d{2}-\d{2})[ ](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : '';
}

function StatusPill({ row }: { row: PersonalizedDocumentRow }) {
  if (row.Status !== 1) {
    return <span className="pdl-pill pdl-pill-withdrawn">Withdrawn</span>;
  }
  const status = row.windowStatus || 'live';
  if (status === 'scheduled') {
    return <span className="pdl-pill pdl-pill-scheduled">Scheduled</span>;
  }
  if (status === 'expired') {
    return <span className="pdl-pill pdl-pill-expired">Expired</span>;
  }
  return <span className="pdl-pill pdl-pill-live">Live</span>;
}

/**
 * Library view — embedded as the "Library" tab inside the Personalized
 * Documents page. Renders content only (no page root/header); the parent owns
 * the page chrome. Styled with the same premium `pd-*` vocabulary as the
 * upload wizard.
 */
const PersonalizedDocumentsLibrary: React.FC = () => {
  const [documentTypes, setDocumentTypes] = useState<PersonalizedDocumentType[]>([]);

  // Filters
  const [documentTypeId, setDocumentTypeId] = useState<number | ''>('');
  const [studentSearch, setStudentSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | ''>(1);

  // Results
  const [rows, setRows] = useState<PersonalizedDocumentRow[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Withdraw modal
  const [withdrawTarget, setWithdrawTarget] = useState<PersonalizedDocumentRow | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);

  // Bulk visibility-window edit
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [windowModalOpen, setWindowModalOpen] = useState(false);
  const [modalFrom, setModalFrom] = useState('');
  const [modalUntil, setModalUntil] = useState('');
  const [savingWindow, setSavingWindow] = useState(false);

  useEffect(() => {
    fetchDocumentTypes()
      .then(setDocumentTypes)
      .catch(() => setDocumentTypes([]));
  }, []);

  const load = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      setError(null);
      try {
        const params: ListAllParams = {
          documentTypeId,
          studentSearch: studentSearch.trim(),
          fromDate,
          toDate,
          status: statusFilter,
          page: targetPage,
          pageSize: PAGE_SIZE,
        };
        const res = await listAll(params);
        setRows(res.rows);
        setTotal(res.pagination.total);
        setTotalPages(res.pagination.totalPages);
        setPage(res.pagination.page);
        setSelectedIds(new Set());
      } catch (err: any) {
        const msg =
          err?.response?.data?.error?.message || err?.message || 'Failed to load documents.';
        setError(msg);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [documentTypeId, studentSearch, fromDate, toDate, statusFilter],
  );

  // Initial load
  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = () => load(1);

  const openDoc = async (row: PersonalizedDocumentRow, disposition: 'inline' | 'attachment') => {
    try {
      const url = await getSignedDocumentUrl(row.Id, disposition);
      if (!url) throw new Error('No signed URL returned.');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message || err?.message || 'Could not open the document.';
      setError(msg);
    }
  };

  const confirmWithdraw = async () => {
    if (!withdrawTarget) return;
    setWithdrawing(true);
    try {
      await withdrawDocument(withdrawTarget.Id);
      setWithdrawTarget(null);
      load(page);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message || err?.message || 'Could not withdraw the document.';
      setError(msg);
    } finally {
      setWithdrawing(false);
    }
  };

  // --- Selection (active rows only) ---
  const selectableRows = rows.filter((r) => r.Status === 1);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.Id));

  const toggleRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (selectableRows.every((r) => prev.has(r.Id))) return new Set();
      return new Set(selectableRows.map((r) => r.Id));
    });
  };

  // --- Bulk window edit ---
  const openWindowModal = () => {
    if (selectedIds.size === 0) return;
    if (selectedIds.size === 1) {
      const only = rows.find((r) => selectedIds.has(r.Id));
      setModalFrom(toLocalInput(only?.VisibleFrom));
      setModalUntil(toLocalInput(only?.VisibleUntil));
    } else {
      setModalFrom('');
      setModalUntil('');
    }
    setWindowModalOpen(true);
  };

  const saveWindow = async () => {
    if (selectedIds.size === 0) return;
    if (modalFrom && modalUntil && modalFrom > modalUntil) {
      setError('Visible from must be on or before Visible until.');
      return;
    }
    setSavingWindow(true);
    try {
      await updateWindow(Array.from(selectedIds), {
        visibleFrom: modalFrom,
        visibleUntil: modalUntil,
      });
      setWindowModalOpen(false);
      load(page);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.message || err?.message || 'Could not update visibility window.';
      setError(msg);
    } finally {
      setSavingWindow(false);
    }
  };

  return (
    <div className="pdl-view">
      {/* Filters */}
      <div className="pdl-filters">
        <div>
          <label className="pd-field-label" htmlFor="pdl-type">Document type</label>
          <select
            id="pdl-type"
            className="pd-field-select"
            value={documentTypeId}
            onChange={(e) => setDocumentTypeId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">All types</option>
            {documentTypes.map((t) => (
              <option key={t.Id} value={t.Id}>{t.Name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="pd-field-label" htmlFor="pdl-student">Student (name or code)</label>
          <input
            id="pdl-student"
            className="pd-field-input"
            type="text"
            placeholder="e.g. S2 or Pranitha"
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSearch(); }}
          />
        </div>
        <div>
          <label className="pd-field-label" htmlFor="pdl-from">Uploaded from</label>
          <input id="pdl-from" className="pd-field-input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="pd-field-label" htmlFor="pdl-to">Uploaded to</label>
          <input id="pdl-to" className="pd-field-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div>
          <label className="pd-field-label" htmlFor="pdl-status">Status</label>
          <select
            id="pdl-status"
            className="pd-field-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value={1}>Active</option>
            <option value={2}>Withdrawn</option>
            <option value="">All</option>
          </select>
        </div>
        <div className="pdl-filter-actions">
          <button className="di-btn-primary" onClick={onSearch} disabled={loading}>
            {loading ? <Loader size={16} className="di-spin" /> : <Search size={16} />} Search
          </button>
        </div>
      </div>

      {error && (
        <div className="di-error-banner">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Bulk action bar */}
      <div className="pdl-bulkbar">
        <span className="pdl-bulkbar-info">
          {selectedIds.size > 0
            ? `${selectedIds.size} selected`
            : 'Select rows to set their visibility window'}
        </span>
        <button
          className="di-btn-primary"
          onClick={openWindowModal}
          disabled={selectedIds.size === 0}
        >
          <CalendarClock size={16} /> Set visibility window
          {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
        </button>
      </div>

      {/* Table */}
      <div className="di-table-wrapper">
        <table className="di-preview-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={selectableRows.length === 0}
                  title="Select all active on this page"
                />
              </th>
              <th>Student</th>
              <th>Batch</th>
              <th>Type</th>
              <th>Title</th>
              <th>Size</th>
              <th>Uploaded</th>
              <th>Visible from</th>
              <th>Visible until</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={11} className="pdl-empty"><Loader size={28} className="di-spin" /><div>Loading…</div></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} className="pdl-empty"><div><Inbox size={32} /></div>No documents match these filters.</td></tr>
            ) : (
              rows.map((row) => (
                <tr key={row.Id}>
                  <td>
                    {row.Status === 1 && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.Id)}
                        onChange={() => toggleRow(row.Id)}
                      />
                    )}
                  </td>
                  <td className="di-td-details" title={`${row.StudentName || ''} (${row.StudentCode || ''})`}>
                    <div className="pdl-student-cell">
                      <div className="pd-row-avatar">{(row.StudentName || row.StudentCode || '?').slice(0, 1).toUpperCase()}</div>
                      <div style={{ minWidth: 0 }}>
                        <div className="pdl-student-name">{row.StudentName || '—'}</div>
                        <div className="pdl-muted" style={{ fontSize: 12 }}>{row.StudentCode || ''}</div>
                      </div>
                    </div>
                  </td>
                  <td title={row.BatchName || ''}>{row.BatchName || '—'}</td>
                  <td>{row.DocumentTypeName}</td>
                  <td className="di-td-details" title={row.Name || ''}>{row.Name || '—'}</td>
                  <td>{formatBytes(row.SizeBytes)}</td>
                  <td>{formatDate(row.UploadedDate)}</td>
                  <td>{row.VisibleFrom ? formatDate(row.VisibleFrom) : <span className="pdl-muted">—</span>}</td>
                  <td>{row.VisibleUntil ? formatDate(row.VisibleUntil) : <span className="pdl-muted">—</span>}</td>
                  <td><StatusPill row={row} /></td>
                  <td>
                    <div className="pdl-row-actions">
                      {row.Status === 1 ? (
                        <>
                          <button className="pdl-icon-btn" onClick={() => openDoc(row, 'inline')} title="View">
                            <Eye size={14} /> View
                          </button>
                          <button className="pdl-icon-btn" onClick={() => openDoc(row, 'attachment')} title="Download">
                            <Download size={14} />
                          </button>
                          <button
                            className="pdl-icon-btn pdl-icon-btn-danger"
                            onClick={() => setWithdrawTarget(row)}
                            title="Withdraw"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      ) : (
                        <span className="pdl-muted" style={{ fontSize: 12 }}>—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pdl-pagination">
        <div className="pdl-pagination-info">
          {total === 0 ? 'No results' : `Showing page ${page} of ${totalPages} · ${total} document${total !== 1 ? 's' : ''}`}
        </div>
        <div className="pdl-pagination-controls">
          <button className="pdl-icon-btn" onClick={() => load(page - 1)} disabled={loading || page <= 1}>
            <ChevronLeft size={14} /> Prev
          </button>
          <button className="pdl-icon-btn" onClick={() => load(page + 1)} disabled={loading || page >= totalPages}>
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Withdraw confirm */}
      <Modal show={!!withdrawTarget} onHide={() => setWithdrawTarget(null)} centered dialogClassName="pd-confirm-modal">
        <Modal.Header>
          <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>Withdraw document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ margin: 0 }}>
            Withdraw <strong>{withdrawTarget?.Name || withdrawTarget?.FileName}</strong> for{' '}
            <strong>{withdrawTarget?.StudentName || withdrawTarget?.StudentCode}</strong>? The
            student will no longer see it in iLearn. This can be re-uploaded later.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setWithdrawTarget(null)} disabled={withdrawing}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmWithdraw} disabled={withdrawing}>
            {withdrawing ? 'Withdrawing…' : 'Withdraw'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Visibility window editor */}
      <Modal show={windowModalOpen} onHide={() => setWindowModalOpen(false)} centered dialogClassName="pd-confirm-modal">
        <Modal.Header>
          <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
            Set visibility window
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginTop: 0, fontSize: 14, color: 'var(--text-body)' }}>
            Applies to <strong>{selectedIds.size}</strong> selected document
            {selectedIds.size !== 1 ? 's' : ''}. Leave a field blank to clear that
            bound — empty <em>from</em> = visible immediately, empty <em>until</em> = no
            end date.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label className="pd-field-label" htmlFor="pdl-modal-from">Visible from</label>
              <input
                id="pdl-modal-from"
                className="pd-field-input"
                type="datetime-local"
                value={modalFrom}
                onChange={(e) => setModalFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="pd-field-label" htmlFor="pdl-modal-until">Visible until</label>
              <input
                id="pdl-modal-until"
                className="pd-field-input"
                type="datetime-local"
                value={modalUntil}
                onChange={(e) => setModalUntil(e.target.value)}
              />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              className="pdl-icon-btn"
              type="button"
              onClick={() => { setModalFrom(''); setModalUntil(''); }}
            >
              Clear both (always visible)
            </button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setWindowModalOpen(false)} disabled={savingWindow}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveWindow} disabled={savingWindow}>
            {savingWindow ? 'Saving…' : 'Save window'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PersonalizedDocumentsLibrary;
